// The detection a chat is about, when it was opened from a detection link
// (`?detection_id=` — the SIEM side links to us; the id is opaque to us, which is
// what keeps this SIEM-agnostic).
//
// The id is a LOCATION, not state: it lives in the URL, so a reload and a shared
// link both land on it. As soon as the detection loads, HomePage opens a chat for
// it (POST /api/sessions with the id, no agent turn); the server saves the id on
// that chat and the chats list carries it — which is what keeps the card on a chat
// reopened days later from the sidebar, where the URL is bare.
import type { Detection } from '@/services/vhSearch';

export const DETECTION_PARAM = 'detection_id';

/** The id the link names, or null when there is no usable one. Blank counts as
 *  none: an empty param is a broken link, not a detection called "". So do `.`
 *  and `..` — `encodeURIComponent` leaves them as they are, and in the request
 *  path they would walk up a segment instead of naming a detection. */
export function readDetectionId(search: URLSearchParams): string | null {
  const id = search.get(DETECTION_PARAM)?.trim();
  return id && id !== '.' && id !== '..' ? id : null;
}

/** The detection the open chat is about. The saved one wins; the URL covers the
 *  stretch before the chats list has caught up with the chat just opened for it. */
export function linkedDetectionId(
  session: { detection_id?: string | null } | undefined,
  search: URLSearchParams,
): string | null {
  return session?.detection_id || readDetectionId(search);
}

/** What the detection card shows. `not_found` and `error` are kept apart on
 *  purpose: an air2-api outage must never read as "this detection doesn't exist". */
export type DetectionState =
  | { status: 'loading' }
  | { status: 'found'; detection: Detection }
  | { status: 'not_found' }
  | { status: 'error'; retry: () => void };

